import SeoHead from '@atoms/SeoHead';
import { getAllCategories, getAllPosts } from '@lib/sanity';
import Layout from '@molecules/Layout';
import type { LayoutProps } from '@molecules/Layout/Layout.types';
import type { ListingCardProps } from '@organisms/ListingCard/ListingCard.types';
import { getClient } from '@sanity/lib/getClient';
import SearchPageView from '@views/SearchPageView';
import type { SearchPageViewProps } from '@views/SearchPageView/SearchPageView.types';
import type { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';

import { mapArticleCard } from '@/mappers/articleCard';
import { mapLayout } from '@/mappers/layout';
import { shortenText } from '@/utils';

interface SearchPageProps {
  allPosts: ListingCardProps[];
  layoutData: Omit<LayoutProps, 'children'>;
}

const SearchPage = ({
  allPosts,
  layoutData,
}: SearchPageProps): ReactElement => {
  const router = useRouter();
  const q = router.query.q;

  const filterArticles = ({ articleList, searchTerm }: SearchPageViewProps) => {
    if (!searchTerm) return null;
    const searchTerms = searchTerm?.split(' ').filter(Boolean);

    return articleList.filter(article =>
      searchTerms.some(term => {
        if (
          article.title.toLowerCase().includes(term.toLowerCase()) ||
          article.excerpt.toLowerCase().includes(term.toLowerCase()) ||
          article.tags.some(tag =>
            tag.title.toLowerCase().includes(term.toLowerCase()),
          )
        ) {
          return article;
        }
      }),
    );
  };

  const filteredArticles = filterArticles({
    articleList: allPosts,
    searchTerm: q as string,
  });

  return (
    <>
      <SeoHead
        title={`Szukaj: ${q as String}`}
        metaDescription="Strona z wynikami wyszukiwania"
      />
      <Layout {...layoutData}>
        <SearchPageView
          articleList={filteredArticles}
          searchTerm={q && shortenText(String(q))}
        />
      </Layout>
    </>
  );
};

export const getStaticProps: GetStaticProps<SearchPageProps> = async () => {
  const client = getClient();

  const tags = await getAllCategories(client);
  const posts = await getAllPosts(client);

  return {
    props: {
      allPosts: posts.map(mapArticleCard),
      layoutData: mapLayout(tags),
    },
  };
};

export default SearchPage;
